<template>
  <button
    :class="buttonClasses"
    class="app-button td-800"
    :disabled="disabled"
    @click.prevent.stop="onClickButton"
  >
    <slot />
  </button>
</template>
<script>
export const STATE_TRANSITION_UP_SHOW = 'transition-up-show';
export const STATE_TRANSITION_UP_HIDE = 'transition-up-hide';
export const STATE_HIDE_INSTANT = 'hide-instant';
export const STATE_HIDE = 'hide';

export const POSITION_RELATIVE = 'relative';
export const POSITION_FIXED = 'fixed';

export const COLOR_BLUE = 'blue';
export const COLOR_WHITE = 'white';
export const COLOR_GREY = 'grey';

export const SIZE_SMALL = 'small';
export const SIZE_MEDIUM = 'medium';

export default {
  name: 'AppButton',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: POSITION_RELATIVE,
      required: false,
    },
    color: {
      type: String,
      default: COLOR_BLUE,
      required: false,
    },
    state: {
      type: String,
      default: null,
      required: false,
    },
    size: {
      type: String,
      default: SIZE_MEDIUM,
      required: false,
    },
  },
  emits: ['click'],
  computed: {
    buttonClasses() {
      const buttonClasses = [
        `app-button-state-${this.state || 'none'}`,
        `app-button-color-${this.color || COLOR_BLUE}`,
        `app-button-position-${this.position || POSITION_RELATIVE}`,
        `app-button-size-${this.size || SIZE_MEDIUM}`,
      ];

      if (this.disabled) {
        buttonClasses.push('app-button-disabled');
      }

      return buttonClasses;
    },
  },
  methods: {
    onClickButton() {
      if (this.disabled) {
        return;
      }

      this.$emit('click');
    },
  },
};
</script>
<style lang="scss" scoped>
$buttonHeight: 50px;
$borderRadius: 8px;

$colorBlueBg: #45A4CD;
$colorBlueText: #EDF1F4;

$colorWhiteBg: #EDF1F4;
$colorWhiteText: #063C5A;

$colorGreyBg: #C7D3DA;
$colorGreyText: #45A4CD;

@mixin appButtonColors($name, $backgroundColor, $textColor) {
  &.app-button-color-#{$name} {
    background-color: $backgroundColor;
    color: $textColor;

    &.app-button-position-relative {
      &:hover {
        background-color: lighten($backgroundColor, 5%);
      }

      &:active {
        background-color: lighten($backgroundColor, 5%);
        box-shadow: inset 4px 4px 5px 0px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.app-button {
  height: $buttonHeight;
  line-height: 100%; // $buttonHeight does not look correct on firefox android
  cursor: pointer;
  outline: 0;
  border: 0;
  font-weight: 600;

  user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;

  width: 100%;
  text-align: center;

  &.app-button-disabled {
    opacity: .8;
    cursor: not-allowed;
  }

  &:focus {
    outline: 0;
    border: 0;
  }

  transition: bottom 800ms cubic-bezier(0.860, 0.000, 0.070, 1.000),
    height 800ms cubic-bezier(0.860, 0.000, 0.070, 1.000),
    color 200ms ease-in-out,
    background-color 200ms ease-in-out,
    box-shadow 200ms ease-in-out;

  // Colors
  @include appButtonColors('blue', $colorBlueBg, $colorBlueText);
  @include appButtonColors('white', $colorWhiteBg, $colorWhiteText);
  @include appButtonColors('grey', $colorGreyBg, $colorGreyText);

  // Sizes
  &.app-button-size-small {
    font-size: 12pt;
  }

  &.app-button-size-medium {
    font-size: 16pt;
  }

  &.app-button-size-block {
    font-size: 12pt;
    width: 100% !important;
    max-width: auto !important;
  }

  // Positions
  &.app-button-position-relative {
    position: relative;
    border-radius: $borderRadius;
    padding: 5px 10px;
    max-width: 500px;
    width: auto;
  }

  &.app-button-position-fixed {
    padding: 0;
    position: fixed;
    bottom: -$buttonHeight;
    left: 0;
    border-radius: $borderRadius $borderRadius 0 0;

    z-index: 100000;
  }

  // States - NOTE: states are only relevant for fixed buttons
  &.app-button-position-fixed {
    &.app-button-state-peak {
      bottom: 0;
    }

    &.app-button-state-transition-up-show {
      color: transparent !important;
      cursor: default;
      height: 100vh;
      bottom: 0;
      border-radius: 0 0 0 0;
    }

    &.app-button-state-hide-instant {
      transition-duration: 0ms;
      bottom: -$buttonHeight;
    }

    &.app-button-state-hide {
      bottom: -$buttonHeight;
    }

    &.app-button-state-transition-up-hide {
      color: transparent !important;
      cursor: default;
      height: 0;
      top: 0;
      border-radius: 0 0 8px 8px;
    }
  }
}
</style>
